import {
    Grid,
    Box,
    Typography, Card, CardContent, Button,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { Done, Error, CheckCircle } from "@mui/icons-material";
import React, {useMemo} from "react";
import { noun as plural } from "plural-ru";
import {CreateTerminalReleaseResult} from "~/apps/packages/warehouse-closure/queries";
import {PortTheme} from "~/ui/theme";
import {TypographyWithIcon} from "~/apps/core/ui/TypographyWithIcon";
import {FloatingActions} from "~/apps/core/ui/FloatingActions";
import {DoneFilled} from "~/ui/icons";


export type TerminalReleaseFormResultProps = {
    data: CreateTerminalReleaseResult["CreateTerminalRelease"]["terminal_release"]
    onContinue?: () => void
}


const useStyles = makeStyles<PortTheme>(theme => ({
    badgeError: {
        color: "#C83C33",
        width: 12,
        height: 12,
    },
    badgeSuccess: {
        color: "#3F79B5",
        width: 12,
        height: 12
    },
    iconError: {
        backgroundColor: "#C83C33",
        borderColor: "#C83C331A",
    },
    iconSuccess: {
        backgroundColor: "#3F79B5",
        borderColor: "#009ACF19",
    },
    icon: {
        width: 100,
        height: 100,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#FFFFFF",
        padding: 22,
        fontWeight: 700,
        backgroundClip: "padding-box",
        borderRadius: 106,
        borderStyle: "solid",
        borderWidth: 15,
        "& .MuiSvgIcon-root": {
            width: 35,
            height: 35,
        }
    },
}));


export const TerminalReleaseFormResult = (props: TerminalReleaseFormResultProps) => {
    const {data, onContinue} = props;
    const classes = useStyles(props);
    const [requestsWithError, requestsWithoutError] = useMemo(() => {
        const requestsWithError = [], requestsWithoutError = [];
        for (const item of data) {
            if (item.code != 0) {
                requestsWithError.push(item);
            } else {
                requestsWithoutError.push(item);
            }
        }
        return [requestsWithError, requestsWithoutError];
    }, [data]);
    const iconContainerClassName = [
        classes.icon,
        requestsWithError.length == 0 ? classes.iconSuccess: classes.iconError
    ].join(" ");

    return (
        <>
            <Card>
                <CardContent>
                    <Grid container direction="column" alignItems="center">
                        <Grid item>
                            <div className={iconContainerClassName}>
                                {requestsWithError.length == 0 ? (
                                    <Done/>
                                ) : (
                                    <Error/>
                                )}
                            </div>
                        </Grid>
                        <Grid item marginTop="32px">
                            <Typography variant="screenTitle" color="primary" align="center" display="block">
                                {requestsWithError.length == 0 ? "Заявки успешно созданы" : "Не удалось создать заявки"}
                            </Typography>
                        </Grid>
                        <Grid item marginTop="11px">
                            <Typography variant="screenText" color="secondary" align="center" display="block">
                                {requestsWithoutError.length > 0 && (
                                    plural(requestsWithoutError.length, "Создана %d заявка", "Создано %d заявки", "Создано %d заявок")
                                )}
                                <br/>
                                {requestsWithError.length > 0 && (
                                    plural(requestsWithError.length, "Ошибка в %d заявке", "Ошибка в %d заявках", "Ошибка в %d заявках")
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Grid container direction="column" wrap="nowrap" spacing="8px" marginTop="16px">
                {requestsWithError.map((item, index) => (
                    <Grid item key={index}>
                        <Card>
                            <CardContent>
                                <Typography variant="cardTitle1" display="block" marginBottom="4px">
                                    {item.container}
                                </Typography>
                                <TypographyWithIcon startIcon={<Error className={classes.badgeError}/>}
                                                    variant="cardText" display="block" color="secondary">
                                    {item.message || "Ошибка"}
                                </TypographyWithIcon>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {requestsWithoutError.map((item, index) => (
                    <Grid item key={index}>
                        <Card>
                            <CardContent>
                                <Typography variant="cardTitle1" display="block" marginBottom="4px">
                                    {item.container}
                                </Typography>
                                <TypographyWithIcon startIcon={<CheckCircle className={classes.badgeSuccess}/>}
                                            variant="cardText" display="block" color="secondary">
                                    Заявка успешно создана
                                </TypographyWithIcon>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Box marginTop="80px"/>
            <FloatingActions>
                <Button fullWidth onClick={onContinue} startIcon={<DoneFilled/>}>
                    Готово
                </Button>
            </FloatingActions>
        </>
    );
}